import { ComponentType, lazy, LazyExoticComponent } from 'react';
import {
  claimStaleBundleReload,
  reloadWithFreshDocument
} from 'utils/staleBundle';

/**
 * A dynamic import can fail after a deploy when the requested chunk filename no
 * longer exists (content-hash renamed) — the SPA server then serves index.html for
 * the missing `.js`, which surfaces as one of these browser-specific messages:
 *   - Chrome/Edge: "Failed to fetch dynamically imported module"
 *   - Safari:      "Importing a module script failed"
 *   - Safari/strict: "'text/html' is not a valid JavaScript MIME type"
 * We deliberately do NOT match a bare "Load failed"/"Failed to fetch" so generic
 * network errors don't trigger a reload.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /dynamically imported module|module script|mime type/i;

export const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: string } | null)?.message ?? '';
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
};

/**
 * Wraps a dynamic-import factory so a stale-chunk failure triggers a one-time reload
 * (to fetch the fresh index.html + chunk manifest) instead of crashing into the
 * Suspense error boundary. The reload refetches index.html past the HTTP cache first
 * (a plain reload can come back with the cached old index.html), and a short time-window
 * guard (utils/staleBundle) prevents a reload loop: if the chunk is still missing right
 * after the reload (genuinely gone, not just stale), the error propagates so the boundary
 * can render. Exported separately from `lazyWithReload` so the recovery logic can be
 * unit-tested without rendering.
 */
export const reloadingImport =
  <T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    chunkName: string
  ) =>
  (): Promise<{ default: T }> =>
    importFn().catch((error: unknown) => {
      if (isChunkLoadError(error) && claimStaleBundleReload()) {
        console.warn(
          `Chunk "${chunkName}" is from an older deploy. Reloading to fetch the current build.`
        );
        void reloadWithFreshDocument();
        // Hold Suspense pending through the reload so the error screen never flashes.
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    });

/**
 * Drop-in replacement for React.lazy that recovers from a stale-chunk import failure
 * by reloading once. `chunkName` names the chunk in the reload warning.
 */
export const lazyWithReload = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName: string
): LazyExoticComponent<T> => lazy(reloadingImport(importFn, chunkName));

/**
 * Vite dispatches `vite:preloadError` when its dynamic-import preload helper fails to
 * load a chunk. Registering this catches dynamic imports not wrapped by
 * `lazyWithReload` (route preloads, the Braze SDK, etc.) with the same one-time
 * staleness recovery. Cancelling the event stops Vite from rethrowing the import error
 * into React while the reload is under way; once the guard says we already reloaded, the
 * event is left alone so the error reaches the error boundary.
 */
export const listenForChunkPreloadErrors = (): void => {
  window.addEventListener('vite:preloadError', event => {
    if (!claimStaleBundleReload()) return;
    event.preventDefault();
    void reloadWithFreshDocument();
  });
};
